import { useEffect, useMemo, useRef, useState } from 'react';
import type { MetricPointDto, MetricsRange } from '@cpm/shared';
import { cx } from './ui.js';

/**
 * Dependency-free SVG usage charts (ADR-0012). One measure per chart
 * (never dual axes): CPU % and memory MB render side by side. Each chart
 * is a single series — an hourly average line with a translucent band up
 * to the hourly peak — so identity comes from the title, values from the
 * crosshair tooltip and the table view (which also satisfies the palette's
 * light-mode contrast relief for the memory hue). Gaps in collection stay
 * gaps: segments are only connected between adjacent hours, missing hours
 * are never interpolated.
 */

export type MetricKind = 'cpu' | 'memory';

export function formatPercent(value: number): string {
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)}%`;
}

export function formatMb(value: number): string {
  if (value >= 1024) {
    const gb = value / 1024;
    return `${gb >= 10 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
  }
  return `${Math.round(value)} MB`;
}

export function formatMetricValue(kind: MetricKind, value: number): string {
  return kind === 'cpu' ? formatPercent(value) : formatMb(value);
}

/** Relative timestamps, one rounding policy for every panel. */
export function timeAgo(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 2) return 'just now';
  if (minutes < 90) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 36 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
}

export function formatBucketLabel(iso: string, range: MetricsRange): string {
  const date = new Date(iso);
  if (range === '24h') {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatBucketTooltip(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Round a scale ceiling up to a friendly 1/2/5 step. */
function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const HOUR_MS = 3_600_000;
const HEIGHT = 190;
const PAD = { top: 14, right: 12, bottom: 22, left: 46 };

interface ChartPoint {
  x: number;
  yAvg: number;
  yMax: number;
  point: MetricPointDto;
}

export function RangeTabs({
  value,
  onChange,
}: {
  value: MetricsRange;
  onChange: (range: MetricsRange) => void;
}) {
  return (
    <div
      className="inline-flex rounded-lg bg-slate-100 p-0.5 text-xs font-medium dark:bg-slate-800"
      role="tablist"
      aria-label="Time range"
    >
      {(['24h', '7d', '30d'] as const).map((range) => (
        <button
          key={range}
          role="tab"
          aria-selected={value === range}
          onClick={() => onChange(range)}
          className={cx(
            'rounded-md px-2.5 py-1 transition-colors',
            value === range
              ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-600 dark:text-white'
              : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
          )}
        >
          {range}
        </button>
      ))}
    </div>
  );
}

/**
 * Hourly average line with a band to the hourly peak, on a zero baseline.
 * Crosshair + tooltip ship by default; the chart is one series, so the
 * title carries identity (no legend box).
 */
export function UsageChart({
  kind,
  title,
  points,
  range,
  peakLabel = 'peak',
}: {
  kind: MetricKind;
  title: string;
  points: MetricPointDto[];
  range: MetricsRange;
  /** How the band reads in the tooltip ("peak", "combined peak"…). */
  peakLabel?: string;
}) {
  const [wrapRef, width] = useContainerWidth();
  const [hover, setHover] = useState<number | null>(null);
  const color = kind === 'cpu' ? 'var(--chart-cpu)' : 'var(--chart-memory)';

  const value = (p: MetricPointDto) => (kind === 'cpu' ? p.cpuAvgPercent : p.memoryAvgMb);
  const peak = (p: MetricPointDto) => (kind === 'cpu' ? p.cpuMaxPercent : p.memoryMaxMb);

  const geometry = useMemo(() => {
    if (width === 0 || points.length === 0) return null;
    const times = points.map((p) => new Date(p.bucket).getTime());
    const t0 = Math.min(...times);
    const t1 = Math.max(...times);
    const span = Math.max(t1 - t0, HOUR_MS);
    const innerW = width - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const yMax = niceCeil(Math.max(...points.map(peak), kind === 'cpu' ? 5 : 16));
    const x = (t: number) => PAD.left + ((t - t0) / span) * innerW;
    const y = (v: number) => PAD.top + innerH * (1 - v / yMax);
    const chartPoints: ChartPoint[] = points.map((p, i) => ({
      x: points.length === 1 ? PAD.left + innerW / 2 : x(times[i]!),
      yAvg: y(value(p)),
      yMax: y(peak(p)),
      point: p,
    }));
    // Split into segments of adjacent hours; a missing hour breaks the line.
    const segments: ChartPoint[][] = [];
    let current: ChartPoint[] = [];
    for (let i = 0; i < chartPoints.length; i += 1) {
      if (i > 0 && times[i]! - times[i - 1]! > HOUR_MS) {
        segments.push(current);
        current = [];
      }
      current.push(chartPoints[i]!);
    }
    segments.push(current);
    return { chartPoints, segments, yMax, y, innerH, baseline: PAD.top + innerH };
  }, [width, points, kind]);

  const linePath = (segment: ChartPoint[], pick: (p: ChartPoint) => number) =>
    segment.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${pick(p).toFixed(1)}`).join('');

  const bandPath = (segment: ChartPoint[]) =>
    `${linePath(segment, (p) => p.yMax)}${[...segment]
      .reverse()
      .map((p) => `L${p.x.toFixed(1)},${p.yAvg.toFixed(1)}`)
      .join('')}Z`;

  const areaPath = (segment: ChartPoint[], baseline: number) =>
    `${linePath(segment, (p) => p.yAvg)}L${segment[segment.length - 1]!.x.toFixed(1)},${baseline}L${segment[0]!.x.toFixed(1)},${baseline}Z`;

  const hovered = hover !== null && geometry ? geometry.chartPoints[hover] : null;

  // X tick positions: up to four buckets, spread across the series.
  const ticks = useMemo(() => {
    if (!geometry) return [];
    const n = geometry.chartPoints.length;
    if (n <= 4) return geometry.chartPoints;
    const step = (n - 1) / 3;
    return [0, 1, 2, 3].map((i) => geometry.chartPoints[Math.round(i * step)]!);
  }, [geometry]);

  return (
    <div>
      <p className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">{title}</p>
      <div ref={wrapRef} className="relative">
        {points.length === 0 ? (
          <div className="flex h-[190px] items-center justify-center rounded-lg border border-dashed border-slate-200 text-xs text-slate-400 dark:border-slate-800 dark:text-slate-500">
            No data in this range yet
          </div>
        ) : geometry ? (
          <>
            <svg
              width={width}
              height={HEIGHT}
              role="img"
              aria-label={`${title} — hourly average and ${peakLabel}`}
              onPointerMove={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                const px = event.clientX - rect.left;
                let best = 0;
                let bestDist = Infinity;
                geometry.chartPoints.forEach((p, i) => {
                  const dist = Math.abs(p.x - px);
                  if (dist < bestDist) {
                    bestDist = dist;
                    best = i;
                  }
                });
                setHover(best);
              }}
              onPointerLeave={() => setHover(null)}
            >
              {/* recessive horizontal gridlines + y labels at 0 / ½ / max */}
              {[0, 0.5, 1].map((f) => {
                const yPos = geometry.y(geometry.yMax * f);
                return (
                  <g key={f}>
                    <line
                      x1={PAD.left}
                      x2={width - PAD.right}
                      y1={yPos}
                      y2={yPos}
                      stroke="var(--chart-grid)"
                      strokeWidth={1}
                    />
                    <text
                      x={PAD.left - 6}
                      y={yPos + 3}
                      textAnchor="end"
                      fontSize={10}
                      fill="var(--chart-axis)"
                    >
                      {formatMetricValue(kind, geometry.yMax * f)}
                    </text>
                  </g>
                );
              })}
              {ticks.map((p) => (
                <text
                  key={p.point.bucket}
                  x={p.x}
                  y={HEIGHT - 6}
                  // Edge ticks anchor inward so labels never clip.
                  textAnchor={p.x > width - 60 ? 'end' : p.x < PAD.left + 24 ? 'start' : 'middle'}
                  fontSize={10}
                  fill="var(--chart-axis)"
                >
                  {formatBucketLabel(p.point.bucket, range)}
                </text>
              ))}
              {geometry.segments.map((segment, i) =>
                segment.length === 1 ? (
                  <circle
                    key={i}
                    cx={segment[0]!.x}
                    cy={segment[0]!.yAvg}
                    r={4}
                    fill={color}
                  />
                ) : (
                  <g key={i}>
                    <path d={areaPath(segment, geometry.baseline)} fill={color} opacity={0.08} />
                    <path d={bandPath(segment)} fill={color} opacity={0.18} />
                    <path
                      d={linePath(segment, (p) => p.yAvg)}
                      fill="none"
                      stroke={color}
                      strokeWidth={2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  </g>
                ),
              )}
              {hovered && (
                <g pointerEvents="none">
                  <line
                    x1={hovered.x}
                    x2={hovered.x}
                    y1={PAD.top}
                    y2={geometry.baseline}
                    stroke="var(--chart-crosshair)"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                  />
                  <circle
                    cx={hovered.x}
                    cy={hovered.yAvg}
                    r={4}
                    fill={color}
                    stroke="var(--color-white, #fff)"
                    className="stroke-white dark:stroke-slate-900"
                    strokeWidth={2}
                  />
                </g>
              )}
            </svg>
            {hovered && (
              <div
                className="pointer-events-none absolute top-1 z-10 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-lg dark:bg-slate-700"
                style={{
                  left: Math.min(Math.max(hovered.x, 70), Math.max(width - 70, 70)),
                }}
              >
                <span className="font-medium">{formatBucketTooltip(hovered.point.bucket)}</span>
                <span className="mx-1.5 opacity-50">·</span>
                avg {formatMetricValue(kind, kind === 'cpu' ? hovered.point.cpuAvgPercent : hovered.point.memoryAvgMb)}
                <span className="mx-1.5 opacity-50">·</span>
                {peakLabel} {formatMetricValue(kind, kind === 'cpu' ? hovered.point.cpuMaxPercent : hovered.point.memoryMaxMb)}
              </div>
            )}
          </>
        ) : (
          <div style={{ height: HEIGHT }} />
        )}
      </div>
    </div>
  );
}

/** Accessible data view for the charts (also the contrast-relief channel). */
export function MetricsTable({ points, range }: { points: MetricPointDto[]; range: MetricsRange }) {
  return (
    <div className="max-h-64 overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <tr>
            <th className="px-3 py-2 font-medium">Hour</th>
            <th className="px-3 py-2 text-right font-medium">CPU avg</th>
            <th className="px-3 py-2 text-right font-medium">CPU peak</th>
            <th className="px-3 py-2 text-right font-medium">Memory avg</th>
            <th className="px-3 py-2 text-right font-medium">Memory peak</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 tabular-nums dark:divide-slate-800">
          {[...points].reverse().map((p) => (
            <tr key={p.bucket}>
              <td className="px-3 py-1.5">{formatBucketTooltip(p.bucket)}</td>
              <td className="px-3 py-1.5 text-right">{formatPercent(p.cpuAvgPercent)}</td>
              <td className="px-3 py-1.5 text-right">{formatPercent(p.cpuMaxPercent)}</td>
              <td className="px-3 py-1.5 text-right">{formatMb(p.memoryAvgMb)}</td>
              <td className="px-3 py-1.5 text-right">{formatMb(p.memoryMaxMb)}</td>
            </tr>
          ))}
          {points.length === 0 && (
            <tr>
              <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                No data in this range{range === '24h' ? ' yet' : ''}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Small headline figure ("current CPU"), not a chart. */
export function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 dark:bg-slate-800/60">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
      {hint && <p className="text-[11px] text-slate-400 dark:text-slate-500">{hint}</p>}
    </div>
  );
}
